<template>
    <div class="supercup-tab">
        <div v-if="totalGames === 0" class="no-game">
            <div class="no-game-icon">🏆</div>
            <h3>Матч СуперКубка не найден</h3>
            <p>Для выбранного сезона матч СуперКубка еще не добавлен</p>
        </div>

        <playoff-match v-else-if="totalGames === 1" :game="singleGame" centered />

        <div v-else class="supercup-stages">
            <div class="sub-tabs-switcher">
                <button
                    v-for="tab in stageTabs"
                    :key="tab.key"
                    type="button"
                    class="sub-switch-btn"
                    :class="{
                        active: activeStage === tab.key,
                        'sub-switch-btn--medal': !!tab.medal,
                    }"
                    :aria-label="tab.ariaLabel || undefined"
                    @click="activeStage = tab.key"
                >
                    <img
                        v-if="tab.medal"
                        :src="`/images/icons/medal-${tab.medal}.svg`"
                        alt=""
                        class="sub-switch-btn__medal"
                        aria-hidden="true"
                    />
                    <span v-else class="sub-switch-btn__label">{{ tab.label }}</span>
                    <span v-if="tab.period" class="sub-switch-btn__period">{{ tab.period }}</span>
                </button>
            </div>

            <cup-playoff-matches :playoff-games="activeStageGroup" />
        </div>
    </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import PlayoffMatch from '@/components/features/results/PlayoffMatch.vue';
import CupPlayoffMatches from '@/components/features/results/CupPlayoffMatches.vue';
import { formatGamesPeriod } from '@/utils/gameDateRange';

const STAGE_META = {
    semifinal: { label: 'Полуфиналы' },
    third_place: {
        label: 'Матч за 3 место',
        medal: 'bronze',
        ariaLabel: 'Матч за 3 место',
    },
    final: {
        label: 'Финал',
        medal: 'gold',
        ariaLabel: 'Финал',
    },
};

const props = defineProps({
    stages: {
        type: Array,
        default: () => [],
    },
});

const activeStage = ref(null);

const stages = computed(() => props.stages || []);

const totalGames = computed(() => stages.value.reduce(
    (sum, stage) => sum + (stage.games?.length || 0),
    0,
));

const singleGame = computed(() => stages.value[0]?.games?.[0] ?? null);

const stageTabs = computed(() => stages.value.map((stage) => {
    const key = stage.stage || '';
    const meta = STAGE_META[key] || { label: 'Матчи' };

    return {
        key,
        label: meta.label,
        medal: meta.medal || null,
        ariaLabel: meta.ariaLabel || null,
        period: formatGamesPeriod(stage.games),
        group: stage,
    };
}));

const activeStageGroup = computed(() => {
    const tab = stageTabs.value.find((item) => item.key === activeStage.value);

    return tab ? [tab.group] : [];
});

watch(stageTabs, (tabs) => {
    if (!tabs.some((tab) => tab.key === activeStage.value)) {
        activeStage.value = tabs[0]?.key ?? null;
    }
}, { immediate: true });
</script>

<style lang="scss" scoped>
.supercup-stages {
    .sub-tabs-switcher {
        display: inline-flex;
        gap: 0.25rem;
        background: #f1f5f9;
        padding: 0.25rem;
        border-radius: 6px;
        margin-bottom: 1.25rem;
        flex-wrap: wrap;

        .sub-switch-btn {
            display: inline-flex;
            flex-direction: column;
            align-items: center;
            gap: 0.15rem;
            padding: 0.5rem 1rem;
            background: transparent;
            border: none;
            border-radius: 4px;
            font-size: 0.875rem;
            font-weight: 500;
            color: #275092;
            cursor: pointer;
            transition: all 0.15s ease;
            white-space: nowrap;
            text-align: center;

            &:hover {
                color: #475569;
            }

            &.active {
                background: #fff;
                color: #275092;
                box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
            }

            &--medal {
                padding: 0.35rem 0.5rem;
            }
        }

        .sub-switch-btn__medal {
            width: 22px;
            height: 22px;
            object-fit: contain;
            display: block;
            flex-shrink: 0;
        }

        .sub-switch-btn__period {
            font-size: 0.65rem;
            font-weight: 500;
            line-height: 1.2;
            color: #64748b;
            white-space: nowrap;
        }
    }
}
</style>
